// ─── Hero: Centered Image ─────────────────────────────────────
// Full-screen hero with background image, overlay, and centered text.
// Matches PremiumStory hero exactly.

import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { AnimatedSection, StaggerContainer } from '../../../lib/animations/AnimatedSection';

export default function HeroCenteredImage({ section, data, theme, settings, rsvpRef }) {
  const { title, subtitle, dateText, backgroundImage, introLabel, ctaText, ctaTarget } = data;
  const anim = section.animation || {};

  const scrollToTarget = () => {
    if (ctaTarget && ctaTarget !== 'rsvp') {
      const el = document.querySelector(`[id^="${ctaTarget}-"]`) || document.querySelector(`[data-section-type="${ctaTarget}"]`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    }
    rsvpRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      className="relative flex flex-col items-center justify-center text-center overflow-hidden"
      style={{ minHeight: '100svh' }}
      data-testid="hero-section"
    >
      {/* Background image */}
      {backgroundImage && (
        <div className="absolute inset-0">
          <img src={backgroundImage} alt="" className="w-full h-full object-cover" loading="eager" />
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.45) 100%)',
            }}
          />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 px-6 max-w-sm">
        <StaggerContainer preset={anim.title || 'stagger-text'}>
          <p
            className="text-xs tracking-[0.35em] uppercase mb-6"
            style={{ color: 'rgba(255,255,255,0.7)', fontFamily: theme.eyebrowFont }}
          >
            {introLabel || 'We\'re getting married'}
          </p>
          <h1
            className="text-4xl sm:text-5xl leading-tight mb-4 font-light"
            style={{ color: '#ffffff', textShadow: '0 2px 20px rgba(0,0,0,0.3)', fontFamily: theme.headingFont }}
          >
            {title || 'Our Wedding'}
          </h1>
          <div className="w-12 h-px mx-auto mb-4" style={{ background: theme.accent }} />
          <p className="text-sm tracking-wider" style={{ color: 'rgba(255,255,255,0.8)', fontFamily: theme.eyebrowFont }}>
            {dateText || ''}
          </p>
          {subtitle && (
            <p className="text-xs mt-2 tracking-wider" style={{ color: 'rgba(255,255,255,0.6)', fontFamily: theme.eyebrowFont }}>
              {subtitle}
            </p>
          )}
        </StaggerContainer>
        {ctaText && (
        <AnimatedSection preset={anim.cta || 'fade-up'} delay={0.5}>
          <button
            onClick={scrollToTarget}
            className="mt-10 px-8 py-3 rounded-full text-xs font-medium tracking-widest uppercase transition-all duration-300 hover:scale-105"
            style={{
              background: theme.accent,
              color: '#fff',
              fontFamily: theme.eyebrowFont,
              boxShadow: `0 4px 20px ${theme.accent}44`,
            }}
            data-testid="hero-rsvp-cta"
          >
            {ctaText}
          </button>
        </AnimatedSection>
        )}
      </div>

      {/* Scroll hint */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
      </motion.div>
    </section>
  );
}
